import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Import styles
import './styles/modal.css';
import './styles/header.css';
import './styles/content.css';
import './styles/footer.css';

// Common file types for the dropdown
const FILE_TYPES = [
  { value: 'pdf', label: 'PDF Document' },
  { value: 'doc', label: 'Word Document' },
  { value: 'xls', label: 'Excel Spreadsheet' },
  { value: 'ppt', label: 'PowerPoint' },
  { value: 'fig', label: 'Figma File' },
  { value: 'sketch', label: 'Sketch File' },
  { value: 'psd', label: 'Photoshop File' },
  { value: 'ai', label: 'Illustrator File' },
  { value: 'png', label: 'PNG Image' },
  { value: 'jpg', label: 'JPG Image' },
  { value: 'svg', label: 'SVG Image' },
  { value: 'mp4', label: 'Video File' },
  { value: 'mp3', label: 'Audio File' },
  { value: 'zip', label: 'ZIP Archive' },
  { value: 'csv', label: 'CSV File' },
  { value: 'json', label: 'JSON File' },
  { value: 'html', label: 'HTML File' },
  { value: 'css', label: 'CSS File' },
  { value: 'js', label: 'JavaScript File' },
  { value: 'other', label: 'Other' },
];

// Get file type value from label
const getFileTypeValue = (label) => {
  const found = FILE_TYPES.find(ft => ft.label === label);
  return found ? found.value : 'other';
};

/**
 * Inline edit form for a single requirement item
 */
const InlineEditForm = ({ 
  item, 
  onSave, 
  onCancel, 
  onKeyDown: parentKeyDown 
}) => {
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');
  const [fileType, setFileType] = useState(() => {
    if (item?.fileType) {
      return getFileTypeValue(item.fileType);
    }
    return 'pdf';
  });
  const [customType, setCustomType] = useState(() => {
    if (item?.fileType && getFileTypeValue(item.fileType) === 'other') {
      return item.fileType;
    }
    return '';
  });

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;
    
    const fileTypeLabel = fileType === 'other' 
      ? customType.trim() || 'Other'
      : FILE_TYPES.find(ft => ft.value === fileType)?.label || 'File';
    
    onSave({
      name: name.trim(),
      fileType: fileTypeLabel,
      description: description.trim() || undefined,
    });
  }, [name, description, fileType, customType, onSave]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && name.trim()) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const isEditing = !!item;

  return (
    <div className="deliverables-modal__inline-form">
      <div className="deliverables-modal__form-grid">
        <div className="deliverables-modal__form-row deliverables-modal__form-row--full">
          <label className="deliverables-modal__form-label">Name</label>
          <input
            type="text"
            className="deliverables-modal__input"
            placeholder="e.g. Final Presentation, Brand Guidelines, Project Summary"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </div>

        <div className="deliverables-modal__form-row">
          <label className="deliverables-modal__form-label">File Type</label>
          <select
            className="deliverables-modal__select"
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
            onKeyDown={handleKeyDown}
          >
            {FILE_TYPES.map(ft => (
              <option key={ft.value} value={ft.value}>
                {ft.label}
              </option>
            ))}
          </select>
        </div>

        {fileType === 'other' && (
          <div className="deliverables-modal__form-row">
            <label className="deliverables-modal__form-label">Custom Type</label>
            <input
              type="text"
              className="deliverables-modal__input"
              placeholder="e.g. Markdown, YAML, Text"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        )}

        <div className="deliverables-modal__form-row deliverables-modal__form-row--full">
          <label className="deliverables-modal__form-label">
            Description <span className="deliverables-modal__form-optional">(optional)</span>
          </label>
          <input
            type="text"
            className="deliverables-modal__input"
            placeholder="Briefly describe what this file should contain..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      <div className="deliverables-modal__inline-actions">
        <button
          className="deliverables-modal__btn deliverables-modal__btn--cancel"
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
        <button
          className="deliverables-modal__btn deliverables-modal__btn--add"
          onClick={handleSubmit}
          disabled={!name.trim()}
          type="button"
        >
          {isEditing ? 'Save' : 'Add'}
        </button>
      </div>
    </div>
  );
};

/**
 * DeliverablesModal Component
 * Modal for defining file requirements that must be submitted to complete a mission
 */
const DeliverablesModal = ({
  isOpen,
  onClose,
  taskName = 'Mission',
  deliverables = [],
  onAddDeliverable,
  onRemoveDeliverable,
  onUpdateDeliverable,
}) => {
  const modalRef = useRef(null);
  const bodyRef = useRef(null);
  
  // Which item is being edited (null = none, 'new' = adding new, or an item id)
  const [editingId, setEditingId] = useState(null);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingId) {
          setEditingId(null);
        } else {
          onClose?.();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, editingId]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
    }
  }, [isOpen]);

  // Scroll to bottom when adding new (to show the form)
  useEffect(() => {
    if (editingId === 'new' && bodyRef.current) {
      setTimeout(() => {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }, 50);
    }
  }, [editingId]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSaveNew = useCallback((data) => {
    onAddDeliverable?.(data);
    setEditingId(null);
  }, [onAddDeliverable]);

  const handleSaveEdit = useCallback((id, data) => {
    onUpdateDeliverable?.(id, data);
    setEditingId(null);
  }, [onUpdateDeliverable]);

  if (!isOpen) return null;

  const totalCount = deliverables.length;
  const isAddingNew = editingId === 'new';

  return createPortal(
    <div className="deliverables-modal__backdrop" onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className="deliverables-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="deliverables-modal__noise" />
        
        {/* Header */}
        <div className="deliverables-modal__header">
          <div className="deliverables-modal__icon-wrapper">
            <div className="deliverables-modal__orbit" />
            <div className="deliverables-modal__particle" />
            <div className="deliverables-modal__particle deliverables-modal__particle--secondary" />
            <div className="deliverables-modal__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
          </div>
          <p className="deliverables-modal__question">
            Which files does the user need to submit in order to mark
          </p>
          <h3 className="deliverables-modal__mission-name">
            "{taskName}"
          </h3>
          <p className="deliverables-modal__question-end">
            as complete?
          </p>
        </div>

        {/* Body */}
        <div className="deliverables-modal__body" ref={bodyRef}>
          {totalCount === 0 && !isAddingNew ? (
            <div className="deliverables-modal__empty">
              <div className="deliverables-modal__empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <p className="deliverables-modal__empty-text">No file requirements yet</p>
              <p className="deliverables-modal__empty-hint">
                Define what files must be submitted to complete this mission
              </p>
            </div>
          ) : (
            <div className="deliverables-modal__list">
              {deliverables.map(del => (
                <div key={del.id}>
                  {editingId === del.id ? (
                    // Inline edit form for this item
                    <InlineEditForm
                      item={del}
                      onSave={(data) => handleSaveEdit(del.id, data)}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    // Display mode
                    <div 
                      className="deliverables-modal__item"
                      onClick={() => !editingId && setEditingId(del.id)}
                      title="Click to edit"
                    >
                      <div className="deliverables-modal__item-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="deliverables-modal__item-content">
                        <div className="deliverables-modal__item-header">
                          <span className="deliverables-modal__item-name">{del.name}</span>
                          {del.fileType && (
                            <span className="deliverables-modal__item-filetype">{del.fileType}</span>
                          )}
                        </div>
                        {del.description && (
                          <span className="deliverables-modal__item-description">{del.description}</span>
                        )}
                      </div>
                      <button
                        className="deliverables-modal__item-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveDeliverable?.(del.id);
                        }}
                        title="Remove requirement"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add new form at bottom */}
          {isAddingNew && (
            <div className="deliverables-modal__add-form">
              <div className="deliverables-modal__form-title">New Requirement</div>
              <InlineEditForm
                item={null}
                onSave={handleSaveNew}
                onCancel={() => setEditingId(null)}
              />
            </div>
          )}

          {!editingId && (
            <button
              className="deliverables-modal__add-deliverable"
              onClick={() => setEditingId('new')}
              type="button"
            >
              + Add File Requirement
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="deliverables-modal__footer">
          <button
            className="deliverables-modal__btn deliverables-modal__btn--primary"
            onClick={onClose}
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeliverablesModal;
